import { useEffect, useMemo, useRef, useState } from 'react'
import { imagePreloader } from '~utils'

type ImageCache = {
  promise: Promise<unknown>
  status: 'pending' | 'error' | 'success'
  error?: unknown
  src?: string
}

type UseImageParams = IntersectionObserverInit & {
  src?: string | Blob
}

const cache: Map<string, ImageCache> = new Map<string, ImageCache>()

const useImage = ({ src: srcIn, ...params }: UseImageParams) => {
  const ref = useRef<HTMLImageElement>(null)

  const src = useMemo(() => {
    if (typeof srcIn === 'string') return srcIn
    if (srcIn instanceof Blob) return URL.createObjectURL(srcIn)
    return undefined
  }, [srcIn])

  const [loading, setLoading] = useState(() =>
    src ? !cache.has(src) : false
  )

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      if (srcIn instanceof Blob && src) {
        URL.revokeObjectURL(src)
      }
    }
  }, [srcIn, src])

  useEffect(() => {
    if (!ref.current && cache.has(src)) return
    if (!IntersectionObserver) {
      setLoading(false)
      return
    }

    const observer = new IntersectionObserver(
      (entries, o) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) {
            if (!cache.has(src)) {
              cache.set(src, { promise: imagePreloader(src), status: 'pending' })
              setLoading(true)
            }

            cache
              .get(src)
              .promise.then(() => {
                cache.set(src, { ...cache.get(src), status: 'success', src })
                setLoading(false)
              })
              .catch((error) => {
                cache.set(src, { ...cache.get(src), status: 'error', error })
                setLoading(false)
              })

            o.unobserve(target)
          }
        })
      },
      { ...params }
    )

    observer.observe(ref.current)

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  return {
    ref,
    src,
    loading,
    info: src ? cache.get(src) : undefined,
  }
}

export default useImage
